import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import GtzBookDialog from "@/components/GtzBookDialog";
import FedExBookDialog from "@/components/FedExBookDialog";
import BolDialog from "@/components/workspace/BolDialog";
import BookingConfirmation from "@/components/workspace/BookingConfirmation";
import BookingEmailDialog from "@/components/workspace/BookingEmailDialog";
import {
  Search, Loader2, Zap, MapPin, Package, Plus, Trash2, ExternalLink,
  ArrowRight, CheckCircle2, Truck, Warehouse, ClipboardList,
} from "lucide-react";

const CLASSES = ["50", "55", "60", "65", "70", "77.5", "85", "92.5", "100", "110", "125", "150", "175", "200", "250", "300", "400", "500"];
const ACCESSORIALS = [
  ["liftgate_delivery", "Liftgate Delivery"],
  ["residential_delivery", "Residential Delivery"],
  ["limited_access_delivery", "Limited Access Delivery"],
  ["inside_delivery", "Inside Delivery"],
  ["appointment_required", "Appointment Required"],
  ["notify_before_delivery", "Notify Before Delivery"],
  ["liftgate_pickup", "Liftgate Pickup"],
  ["residential_pickup", "Residential Pickup"],
  ["limited_access_pickup", "Limited Access Pickup"],
  ["hazmat", "Hazmat"],
];
const SHIP_FROM_KEY = "phs_booknow_shipfrom";

// Local calendar date (YYYY-MM-DD) — avoids the UTC off-by-one where toISOString() rolls to tomorrow
// on evening US timezones.
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// Per-NMFC density sub tables (FedEx sales sheet rev 1-6-23)
const DENSITY_11_TIER = [
  [1, "01", "400"], [2, "02", "300"], [4, "03", "250"], [6, "04", "175"], [8, "05", "125"],
  [10, "06", "100"], [12, "07", "92.5"], [15, "08", "85"], [22.5, "09", "70"], [30, "10", "65"],
  [Infinity, "11", "60"],
];
const NMFC_SUB_TABLES = {
  150400: DENSITY_11_TIER,
  156600: DENSITY_11_TIER,
  99950: [[8, "01", "200"], [Infinity, "02", "100"]],
};
const nmfcHint = (item) => {
  const nmfc = String(item.nmfc || "").trim();
  if (!/^\d{3,6}$/.test(nmfc)) return null;
  const L = parseFloat(item.length) || 0, W = parseFloat(item.width) || 0, H = parseFloat(item.height) || 0;
  const units = Math.max(parseInt(item.units) || 1, 1);
  let w = parseFloat(item.weight) || 0;
  if (!item.weight_is_total) w *= units;
  const vol = (L * W * H) / 1728;
  if (vol <= 0 || w <= 0) return null;
  const density = (w / units) / vol;
  const table = NMFC_SUB_TABLES[nmfc] || DENSITY_11_TIER;
  const tier = table.find(([max]) => density < max);
  return { sub: tier[1], cls: tier[2], density: density.toFixed(1) };
};
const EMPTY_ITEM = {
  mode: "manual", units: "", handling_unit_type: "Pallet", pieces: "", weight: "",
  weight_is_total: true, length: 48, width: 40, height: 48,
  freight_class: "", nmfc: "", description: "", stackable: false, hazmat: false,
};

function defaultShipFrom() {
  try {
    const saved = JSON.parse(localStorage.getItem(SHIP_FROM_KEY) || "null");
    if (saved) return saved;
  } catch { /* ignore */ }
  return { company: "Premier Handling Solutions", contact_name: "", phone: "", street: "", city: "", state: "", zip: "" };
}

function Fld({ label, value, onChange, testid, className = "", placeholder = "", type = "text" }) {
  return (
    <div className={className}>
      <Label className="label-caps">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder} data-testid={testid} className="mt-1 h-9" />
    </div>
  );
}

function PartyCard({ title, icon: Icon, party, onChange, prefix, accent, warehouses = [], onSaveWarehouse }) {
  const set = (k, v) => onChange({ ...party, [k]: v });
  const pickWarehouse = (id) => {
    const w = warehouses.find((x) => x.id === id);
    if (!w) return;
    onChange({
      ...party,
      company: w.company || "", contact_name: w.contact_name || "", phone: w.contact_phone || "",
      street: w.address1 || "", city: w.city || "", state: w.state || "", zip: w.zip || "",
      ...(prefix === "shipto" && w.contact_email ? { email: w.contact_email } : {}),
    });
    toast.success(`${w.company} loaded into ${prefix === "shipfrom" ? "Ship From" : "Ship To"}.`);
  };
  return (
    <div className="border border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-2">
        <div className={`label-caps flex items-center gap-1.5 ${accent}`}>
          <Icon className="h-3.5 w-3.5" /> {title}
        </div>
        <div className="flex items-center gap-1.5">
          {warehouses.length > 0 && (
            <Select value={undefined} onValueChange={pickWarehouse}>
              <SelectTrigger className="h-7 w-40 text-xs" data-testid={`${prefix}-warehouse-select`}>
                <SelectValue placeholder="Warehouse…" />
              </SelectTrigger>
              <SelectContent>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.company}{w.city ? ` · ${w.city}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {onSaveWarehouse && (
            <Button variant="outline" size="sm" onClick={onSaveWarehouse} data-testid={`${prefix}-save-warehouse`}
              className="h-7 gap-1 px-2 text-[11px]" title="Save this address as a reusable warehouse">
              <Warehouse className="h-3 w-3" /> Save
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4">
        <Fld label="Company" value={party.company} onChange={(v) => set("company", v)} testid={`${prefix}-company`} />
        <Fld label="Contact Name" value={party.contact_name} onChange={(v) => set("contact_name", v)} testid={`${prefix}-contact`} />
        {prefix === "shipto" ? (
          <Fld label="Phone" value={party.phone} onChange={(v) => set("phone", v)} testid={`${prefix}-phone`} />
        ) : (
          <Fld label="Phone" value={party.phone} onChange={(v) => set("phone", v)} testid={`${prefix}-phone`} className="col-span-2" />
        )}
        {prefix === "shipto" && (
          <Fld label="Email" value={party.email} onChange={(v) => set("email", v)} testid={`${prefix}-email`} />
        )}
        <Fld label="Street Address" value={party.street} onChange={(v) => set("street", v)} testid={`${prefix}-street`} className="col-span-2" />
        <Fld label="City" value={party.city} onChange={(v) => set("city", v)} testid={`${prefix}-city`} />
        <div className="grid grid-cols-2 gap-3">
          <Fld label="State" value={party.state} onChange={(v) => set("state", v.toUpperCase())} testid={`${prefix}-state`} placeholder="IL" />
          <Fld label="ZIP" value={party.zip} onChange={(v) => set("zip", v)} testid={`${prefix}-zip`} />
        </div>
      </div>
    </div>
  );
}

function ItemRow({ item, idx, onChange, onRemove, canRemove }) {
  const set = (k, v) => onChange(idx, { ...item, [k]: v });
  return (
    <div className="space-y-2 rounded-md border border-border bg-card/60 p-3" data-testid={`bn-item-${idx}`}>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Fld label="Description" value={item.description} onChange={(v) => set("description", v)} testid={`bn-item-${idx}-desc`} className="col-span-2" placeholder="e.g. Plastic pallets 48x40" />
        <Fld label="NMFC # + Sub" value={item.nmfc} onChange={(v) => set("nmfc", v)} testid={`bn-item-${idx}-nmfc`} placeholder="e.g. 156600-3" />
        {(() => {
          const hint = nmfcHint(item);
          return hint ? (
            <div className="col-span-2 -mt-1 text-[11px] font-semibold text-[hsl(var(--success))]" data-testid={`bn-item-${idx}-nmfc-hint`}>
              Auto sub {hint.sub} · Class {hint.cls} ({hint.density} lbs/ft³) — applied on the BOL
            </div>
          ) : null;
        })()}
        <div>
          <Label className="label-caps">Class</Label>
          <Select value={item.freight_class || undefined} onValueChange={(v) => set("freight_class", v)}>
            <SelectTrigger className="mt-1 h-9" data-testid={`bn-item-${idx}-class`}><SelectValue placeholder="Auto (density)" /></SelectTrigger>
            <SelectContent>{CLASSES.map((c) => <SelectItem key={c} value={c}>Class {c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 md:grid-cols-7">
        <Fld label="Pallets" type="number" value={item.units} onChange={(v) => set("units", v)} testid={`bn-item-${idx}-units`} placeholder="e.g. 2" />
        <Fld label="Pieces" type="number" value={item.pieces} onChange={(v) => set("pieces", v)} testid={`bn-item-${idx}-pieces`} placeholder="e.g. 10" />
        <Fld label={item.weight_is_total ? "Total Wgt (lb)" : "Wgt / piece (lb)"} type="number" value={item.weight} onChange={(v) => set("weight", v)} testid={`bn-item-${idx}-weight`} placeholder="lbs" />
        <Fld label="Length (in)" type="number" value={item.length} onChange={(v) => set("length", v)} testid={`bn-item-${idx}-length`} />
        <Fld label="Width (in)" type="number" value={item.width} onChange={(v) => set("width", v)} testid={`bn-item-${idx}-width`} />
        <Fld label="Height (in)" type="number" value={item.height} onChange={(v) => set("height", v)} testid={`bn-item-${idx}-height`} />
        <div className="flex items-end justify-between gap-2 pb-1">
          <div className="space-y-1.5">
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
              <Checkbox checked={item.weight_is_total} onCheckedChange={(v) => set("weight_is_total", !!v)} data-testid={`bn-item-${idx}-totalwgt`} /> Total wgt
            </label>
            <label className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
              <Checkbox checked={item.stackable} onCheckedChange={(v) => set("stackable", !!v)} data-testid={`bn-item-${idx}-stackable`} /> Stackable
            </label>
          </div>
          {canRemove && (
            <Button variant="ghost" size="icon" onClick={() => onRemove(idx)} data-testid={`bn-item-${idx}-remove`} className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--destructive))]">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function BookNow() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [quoteResults, setQuoteResults] = useState([]);
  const [order, setOrder] = useState(null);
  const [shipFrom, setShipFrom] = useState(defaultShipFrom);
  const [shipTo, setShipTo] = useState({ company: "", contact_name: "", phone: "", email: "", street: "", city: "", state: "", zip: "" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const [parsing, setParsing] = useState(false);

  const parsePaste = async () => {
    if (!pasteText.trim()) return;
    setParsing(true);
    try {
      const { data } = await api.post("/quotes/parse-shipment", { text: pasteText });
      if (!(data.line_items || []).length) {
        toast.error("Couldn't read any shipment lines — check the format and try again.");
        return;
      }
      setItems(data.line_items.map((li) => ({ ...EMPTY_ITEM, ...li })));
      toast.success(`Parsed ${data.line_items.length} line item${data.line_items.length > 1 ? "s" : ""} from your paste.`);
      (data.warnings || []).forEach((w) => toast.warning(w));
      setShowPaste(false);
      setPasteText("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setParsing(false);
    }
  };

  const [acc, setAcc] = useState({});
  const [pickupDate, setPickupDate] = useState(() => todayLocal());
  const [rating, setRating] = useState(false);
  const [rateResp, setRateResp] = useState(null);
  const [quote, setQuote] = useState(null);
  const [selecting, setSelecting] = useState("");
  const [showGtzBook, setShowGtzBook] = useState(false);
  const [showFedexBook, setShowFedexBook] = useState(false);
  const [showRlBook, setShowRlBook] = useState(false);
  const [showBookingEmail, setShowBookingEmail] = useState(false);
  const [warehouses, setWarehouses] = useState([]);

  const loadWarehouses = () =>
    api.get("/addresses", { params: { kind: "warehouse" } }).then((r) => { setWarehouses(r.data); return r.data; }).catch(() => []);
  useEffect(() => {
    api.post("/hubspot/sync-warehouses").catch(() => {}).finally(loadWarehouses);
  }, []);

  const saveWarehouse = async () => {
    if (!shipFrom.company?.trim() || !shipFrom.zip?.trim())
      return toast.error("Company and ZIP are needed to save a warehouse.");
    const body = {
      company: shipFrom.company, contact_name: shipFrom.contact_name || "", contact_phone: shipFrom.phone || "",
      address1: shipFrom.street || "", city: shipFrom.city || "", state: shipFrom.state || "",
      zip: shipFrom.zip, kind: "warehouse", notes: "Saved from Book Now",
    };
    try {
      const existing = warehouses.find((w) => (w.company || "").toLowerCase() === shipFrom.company.trim().toLowerCase());
      if (existing) {
        await api.put(`/addresses/${existing.id}`, { ...existing, ...body });
        toast.success(`Warehouse "${shipFrom.company}" updated.`);
      } else {
        await api.post("/addresses", body);
        toast.success(`Warehouse "${shipFrom.company}" saved.`);
      }
      loadWarehouses();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { localStorage.setItem(SHIP_FROM_KEY, JSON.stringify(shipFrom)); }, [shipFrom]);

  const search = async () => {
    if (q.trim().length < 2) return toast.error("Enter an order #, quote #, or customer name.");
    setSearching(true);
    setResults(null);
    setQuoteResults([]);
    try {
      const [hs, cq] = await Promise.allSettled([
        api.get("/hubspot/lookup", { params: { q: q.trim() } }),
        api.get("/quotes", { params: { search: q.trim() } }),
      ]);
      const hsResults = hs.status === "fulfilled" ? hs.value.data.results : [];
      const cqResults = cq.status === "fulfilled" ? (cq.value.data || []).slice(0, 6) : [];
      setResults(hsResults);
      setQuoteResults(cqResults);
      if (hsResults.length === 0 && cqResults.length === 0) toast.info("No matching HubSpot orders or Commander quotes found.");
      else if (hsResults.length === 1 && cqResults.length === 0) await loadOrder(hsResults[0].deal_id);
      else if (hsResults.length === 0 && cqResults.length === 1) await loadQuote(cqResults[0].id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSearching(false);
    }
  };

  const loadQuote = async (quoteId) => {
    try {
      const { data } = await api.get(`/quotes/${quoteId}`);
      setOrder(null);
      setQuote(data);
      setRateResp((data.rates || []).length ? { rates: data.rates } : null);
      setShipFrom((s) => ({
        company: data.origin_company || s.company, contact_name: data.origin_contact_name || s.contact_name,
        phone: data.origin_contact_phone || s.phone, street: data.origin_address1 || s.street,
        city: data.origin_city || s.city, state: data.origin_state || s.state, zip: data.origin_zip || s.zip,
      }));
      setShipTo({
        company: data.dest_company || data.customer_name || "", contact_name: data.dest_contact_name || "",
        phone: data.dest_contact_phone || "", email: data.dest_contact_email || "",
        street: data.dest_address1 || "", city: data.dest_city || "", state: data.dest_state || "",
        zip: data.destination_zip || "",
      });
      if ((data.line_items || []).length) setItems(data.line_items.map((li) => ({ ...EMPTY_ITEM, ...li })));
      if (data.accessorials) setAcc(data.accessorials);
      const today = todayLocal();
      if ((data.pickup_date || "").slice(0, 10) >= today) setPickupDate(data.pickup_date.slice(0, 10));
      const bookedAlready = data.gtz_bol_number || data.bol_pro_number;
      toast.success(`${data.quote_number} loaded${bookedAlready ? " — already booked" : (data.rates || []).length ? " with its saved rates" : ""}.`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadOrder = async (dealId) => {
    try {
      const { data } = await api.get(`/hubspot/order/${dealId}`);
      setOrder(data);
      setShipTo({ ...data.ship_to });
      setRateResp(null);
      setQuote(null);
      toast.success(`${data.order_number ? `Order #${data.order_number}` : "Order"} loaded — ${data.ship_to.company || data.ship_to.contact_name}`);
      const loc = data.ship_from_location;
      if (loc) {
        const whs = warehouses.length ? warehouses : await loadWarehouses();
        const w = whs.find((x) => x.hubspot_value === loc.value || (x.company || "").toLowerCase() === (loc.label || "").toLowerCase());
        if (w && w.zip) {
          setShipFrom({
            company: w.company || "", contact_name: w.contact_name || "", phone: w.contact_phone || "",
            street: w.address1 || "", city: w.city || "", state: w.state || "", zip: w.zip || "",
          });
          toast.success(`Ship From set to ${loc.label} (from HubSpot).`);
        } else if (w) {
          setShipFrom((s) => ({ ...s, company: w.company || loc.label }));
          toast.warning(`This order ships from "${loc.label}" — add its address once, then hit Save (warehouse).`);
        }
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const getRates = async () => {
    if (!shipFrom.zip?.trim() || !shipTo.zip?.trim()) return toast.error("Ship From and Ship To ZIP codes are required.");
    if (!items.some((i) => i.weight > 0)) return toast.error("Add at least one item with a weight.");
    setRating(true);
    setRateResp(null);
    setQuote(null);
    try {
      const payload = {
        origin_zip: shipFrom.zip.trim(), destination_zip: shipTo.zip.trim(),
        origin_city: shipFrom.city, origin_state: shipFrom.state,
        dest_city: shipTo.city, dest_state: shipTo.state,
        pickup_date: pickupDate,
        customer_name: shipTo.company || shipTo.contact_name || "Unknown",
        quote_ref: order ? `#${order.order_number}` : "",
        notes: order ? `PHS Pallets order #${order.order_number} (HubSpot deal ${order.deal_id})` : "Book Now manual entry",
        origin_company: shipFrom.company, origin_address1: shipFrom.street,
        origin_contact_name: shipFrom.contact_name, origin_contact_phone: shipFrom.phone,
        dest_company: shipTo.company, dest_address1: shipTo.street,
        dest_contact_name: shipTo.contact_name, dest_contact_phone: shipTo.phone, dest_contact_email: shipTo.email,
        line_items: items.map((it) => ({
          ...it,
          units: parseInt(it.units, 10) || 1,
          pieces: parseInt(it.pieces, 10) || 1,
          weight: parseFloat(it.weight) || 0,
          length: parseFloat(it.length) || 0,
          width: parseFloat(it.width) || 0,
          height: parseFloat(it.height) || 0,
        })),
        accessorials: acc,
      };
      const { data } = await api.post("/quotes/rate-multi", payload);
      setRateResp(data);
      setQuote(data.quote);
      toast.success(`${data.rates?.length || 0} rates — saved as ${data.quote?.quote_number}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRating(false);
    }
  };

  const refreshQuote = async () => {
    if (!quote?.id) return;
    try {
      const { data } = await api.get(`/quotes/${quote.id}`);
      setQuote(data);
    } catch { /* ignore */ }
  };

  const selectAndBook = async (rate) => {
    if (!quote?.id) return;
    setSelecting(rate.carrier_code);
    try {
      const { data } = await api.put(`/quotes/${quote.id}`, { selected_carrier_code: rate.carrier_code });
      setQuote(data);
      if (rate.broker === "GLOBALTRANZ") setShowGtzBook(true);
      else if (rate.carrier_code === "RL_CARRIERS") setShowRlBook(true);
      else if (rate.carrier_code === "FEDEX_FREIGHT") setShowFedexBook(true);
      else toast.info(`${rate.carrier_name} selected — book this carrier from the quote page or its portal.`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSelecting("");
    }
  };

  const booked = quote && (quote.gtz_bol_number || quote.bol_pro_number);

  return (
    <div className="space-y-5 animate-fade-up" data-testid="book-now-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-black tracking-tighter">
            <Zap className="h-7 w-7 text-[hsl(var(--primary))]" /> Book Now
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Look up a PHS Pallets order from HubSpot, add the shipping details, rate it, and book — one screen.
          </p>
        </div>
        {quote?.quote_number && (
          <Button variant="outline" size="sm" onClick={() => navigate(`/quotes/${quote.id}`)} data-testid="bn-open-quote">
            {quote.quote_number} <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* ---- Order lookup ---- */}
      <div className="border border-border bg-card p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder="Order #, HubSpot Record ID, or confirmation # (e.g. 67008) — or customer name"
              data-testid="bn-search-input" className="h-10 pl-9" />
          </div>
          <Button onClick={search} disabled={searching} data-testid="bn-search-btn"
            className="h-10 gap-2 bg-[hsl(var(--primary))] px-5 text-white hover:bg-[hsl(var(--primary))]/90">
            {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Find Order
          </Button>
        </div>
        {results && results.length > 1 && (
          <div className="mt-3 space-y-1.5">
            {results.map((r) => (
              <button key={r.deal_id} onClick={() => loadOrder(r.deal_id)} data-testid={`bn-result-${r.deal_id}`}
                className="flex w-full items-center justify-between rounded-md border border-border px-3 py-2 text-left text-sm transition-colors hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5">
                <span className="font-semibold">{r.dealname}</span>
                <span className="flex items-center gap-3 text-xs text-muted-foreground">
                  {r.amount != null && <span className="metric-num">{money(r.amount)}</span>}
                  <span>{r.stage}</span>
                  <span>{(r.created_at || "").slice(0, 10)}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        {order && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 px-3 py-2 text-sm" data-testid="bn-order-banner">
            <span className="font-bold">Order #{order.order_number}</span>
            <span>{order.ship_to.company || order.ship_to.contact_name}</span>
            {order.amount != null && <span className="metric-num">{money(order.amount)}</span>}
            <span className="rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 text-[11px] font-bold text-[hsl(var(--primary))]">{order.stage}</span>
            {order.ship_from_location && (
              <span className="flex items-center gap-1 rounded bg-[hsl(var(--warning))]/15 px-1.5 py-0.5 text-[11px] font-bold text-[hsl(var(--warning))]" data-testid="bn-shipfrom-loc">
                <Warehouse className="h-3 w-3" /> Ships from: {order.ship_from_location.label}
              </span>
            )}
            <a href={order.hubspot_url} target="_blank" rel="noreferrer" className="ml-auto flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline">
              HubSpot <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
      </div>

      {/* ---- Parties ---- */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <PartyCard title="Ship From (PHS)" icon={MapPin} party={shipFrom} onChange={setShipFrom} prefix="shipfrom"
          accent="text-[hsl(var(--primary))]" warehouses={warehouses} onSaveWarehouse={saveWarehouse} />
        <PartyCard title="Ship To (Customer)" icon={MapPin} party={shipTo} onChange={setShipTo} prefix="shipto"
          accent="text-[hsl(var(--success))]" warehouses={warehouses} />
      </div>

      {/* ---- Shipment details ---- */}
      <div className="border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <div className="label-caps flex items-center gap-1.5"><Package className="h-3.5 w-3.5" /> Shipment Details</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowPaste((v) => !v)} data-testid="bn-paste-toggle" className="h-8 gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" /> Paste details
            </Button>
            <Label className="label-caps">Pickup Date</Label>
            <Input type="date" value={pickupDate} min={todayLocal()}
              onChange={(e) => setPickupDate(e.target.value)} data-testid="bn-pickup-date" className="h-8 w-40" />
          </div>
        </div>
        <div className="space-y-3 p-4">
          {showPaste && (
            <div className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3" data-testid="bn-paste-panel">
              <div className="mb-1.5 flex flex-wrap items-center justify-between gap-1">
                <span className="label-caps flex items-center gap-1.5"><ClipboardList className="h-3.5 w-3.5" /> Paste shipment details</span>
                <span className="text-[11px] text-muted-foreground">one line per item, e.g. <code>2 stacks 80pcs 40x48x94@500 lbs</code></span>
              </div>
              <Textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} rows={4}
                placeholder={"1 stack 40pcs 40x48x51@260 lbs\n2 stacks 80pcs 40x48x94@500 lbs"}
                data-testid="bn-paste-textarea" className="font-mono text-xs" />
              <div className="mt-2 flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => { setShowPaste(false); setPasteText(""); }} data-testid="bn-paste-cancel" className="h-8">Cancel</Button>
                <Button size="sm" onClick={parsePaste} disabled={parsing || !pasteText.trim()} data-testid="bn-paste-parse"
                  className="h-8 gap-1.5 bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90">
                  {parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />} Parse &amp; Fill
                </Button>
              </div>
              <p className="mt-1.5 text-[11px] text-muted-foreground">Multiple stacks multiply the weight by the stack count (2 stacks @500 = 1,000 lb total). This replaces the items below.</p>
            </div>
          )}
          {items.map((it, i) => (
            <ItemRow key={i} item={it} idx={i} canRemove={items.length > 1}
              onChange={(idx, v) => setItems(items.map((x, j) => (j === idx ? v : x)))}
              onRemove={(idx) => setItems(items.filter((_, j) => j !== idx))} />
          ))}
          <Button variant="outline" size="sm" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="bn-add-item" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Add Item
          </Button>
          <div className="flex flex-wrap gap-x-5 gap-y-2 border-t border-border pt-3">
            {ACCESSORIALS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                <Checkbox checked={!!acc[k]} onCheckedChange={(v) => setAcc({ ...acc, [k]: !!v })} data-testid={`bn-acc-${k}`} /> {label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={getRates} disabled={rating} data-testid="bn-get-rates"
        className="h-11 w-full gap-2 bg-[hsl(var(--primary))] text-base font-bold text-white hover:bg-[hsl(var(--primary))]/90">
        {rating ? <Loader2 className="h-5 w-5 animate-spin" /> : <Truck className="h-5 w-5" />}
        {rating ? "Rating with live carriers…" : "Get Live Rates"}
      </Button>

      {/* ---- Booking confirmation (once booked) ---- */}
      {booked && (
        <BookingConfirmation q={quote}
          onViewBol={() => navigate(`/quotes/${quote.id}`)}
          onEmail={() => setShowBookingEmail(true)} />
      )}

      {/* ---- Rates ---- */}
      {rateResp && !booked && (
        <div className="border border-border bg-card" data-testid="bn-rates">
          <div className="label-caps flex items-center justify-between border-b border-border px-4 py-2.5">
            <span>Live Rates — select a carrier to book</span>
            {quote?.quote_number && <span className="text-muted-foreground">Saved as {quote.quote_number}</span>}
          </div>
          <div className="divide-y divide-border">
            {(rateResp.rates || []).map((r) => {
              const bookable = r.broker === "GLOBALTRANZ" || r.carrier_code === "RL_CARRIERS" || r.carrier_code === "FEDEX_FREIGHT";
              const selected = quote?.selected_carrier_code === r.carrier_code;
              return (
                <div key={r.carrier_code} className={`flex flex-wrap items-center gap-3 px-4 py-2.5 ${selected ? "bg-[hsl(var(--primary))]/5" : ""}`} data-testid={`bn-rate-${r.carrier_code}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                      {r.carrier_name}
                      {r.broker === "PRIORITY1" && <span className="rounded-sm bg-[#F47C20]/15 px-1 py-px text-[9px] font-bold text-[#F47C20]">via Priority1</span>}
                      {r.broker === "GLOBALTRANZ" && <span className="rounded-sm bg-[#0072CE]/15 px-1 py-px text-[9px] font-bold text-[#0072CE]">via GlobalTranz</span>}
                      {selected && <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {r.transit_days ? `${r.transit_days} days transit` : "Transit N/A"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="metric-num text-base font-bold" data-testid={`bn-cost-${r.carrier_code}`}>{money(r.total_carrier_cost)}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">carrier cost</div>
                  </div>
                  <Button size="sm" onClick={() => selectAndBook(r)} disabled={!!selecting} data-testid={`bn-book-${r.carrier_code}`}
                    variant={bookable ? "default" : "outline"}
                    className={bookable ? "h-8 gap-1.5 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90" : "h-8 gap-1.5"}>
                    {selecting === r.carrier_code ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                    {bookable ? "Book" : "Select"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {quote && (
        <>
          <GtzBookDialog open={showGtzBook} onOpenChange={setShowGtzBook} quote={quote} onBooked={refreshQuote} />
          <FedExBookDialog open={showFedexBook} onOpenChange={setShowFedexBook} quote={quote} onBooked={refreshQuote} />
          <BolDialog open={showRlBook} onOpenChange={setShowRlBook} quote={quote} onGenerated={refreshQuote} />
          <BookingEmailDialog quoteId={quote.id} open={showBookingEmail} onOpenChange={(o) => { setShowBookingEmail(o); if (!o) refreshQuote(); }} />
        </>
      )}
    </div>
  );
}
